import React, { useEffect, useState } from "react";
import {
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import {
  useSetBalanceRow,
  useZeroBalanceRow,
} from "api/leaveReport/balanceRowEdit";

const LEAVE_TYPE_OPTIONS = [
  { value: "annual", label: "Annual Leave" },
  { value: "sick", label: "Sick Leave" },
  { value: "pressingNecessity", label: "Pressing Necessity" },
  { value: "compassionate", label: "Compassionate Leave" },
  { value: "bereavement", label: "Bereavement Leave" },
  { value: "juryDuty", label: "Jury Duty" },
  { value: "election", label: "Election Leave" },
  { value: "bandBusiness", label: "Band Business Leave" },
  { value: "governance", label: "Governance Leave" },
  { value: "education", label: "Education Leave" },
  { value: "professionalDevelopment", label: "Professional Development" },
  { value: "culturalSpiritual", label: "Cultural & Spiritual Leave" },
  { value: "leaveWithoutPay", label: "Leave Without Pay (LWP)" },
];

interface Props {
  opened: boolean;
  onClose: () => void;
  mode: "edit" | "zero";
  userId: string;
  employeeName?: string;
  onDone?: () => void;
}

export function BalanceRowEditModal({
  opened,
  onClose,
  mode,
  userId,
  employeeName,
  onDone,
}: Props) {
  const [leaveType, setLeaveType] = useState<string | null>(null);
  const [allocated, setAllocated] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const setBalance = useSetBalanceRow();
  const zeroBalance = useZeroBalanceRow();

  useEffect(() => {
    if (opened) {
      setLeaveType(null);
      setAllocated("");
      setNotes("");
    }
  }, [opened]);

  const busy = setBalance.isLoading || zeroBalance.isLoading;

  const submit = async () => {
    if (!leaveType) return;
    if (mode === "edit") {
      if (allocated === "" || Number(allocated) < 0) return;
      await setBalance.mutateAsync({
        userId,
        leaveType,
        allocated: Number(allocated),
        notes: notes.trim() || undefined,
      });
    } else {
      await zeroBalance.mutateAsync({
        userId,
        leaveType,
        notes: notes.trim() || undefined,
      });
    }
    onDone?.();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === "edit" ? "Edit Leave Balance" : "Clear Leave Balance"}
      centered
    >
      <Stack spacing="sm">
        {employeeName && (
          <Text size="sm" color="dimmed">
            Employee: <b>{employeeName}</b> (current fiscal year)
          </Text>
        )}
        <Select
          label="Leave Type"
          placeholder="Select a leave type"
          data={LEAVE_TYPE_OPTIONS}
          value={leaveType}
          onChange={setLeaveType}
          withAsterisk
          searchable
        />
        {mode === "edit" ? (
          <NumberInput
            label="Allocated days"
            description="Sets allocated; balance is recomputed as allocated − used − pending."
            min={0}
            precision={2}
            value={allocated === "" ? undefined : allocated}
            onChange={(v) => setAllocated(v === undefined ? "" : v)}
            withAsterisk
          />
        ) : (
          <Text size="sm" color="red">
            This will set allocated / used / pending / balance for the selected
            leave type to 0.
          </Text>
        )}
        <Textarea
          label="Notes (optional)"
          placeholder="Reason for this change"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
        />
        <Group position="right" mt="xs">
          <Button variant="default" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            color={mode === "zero" ? "red" : "blue"}
            loading={busy}
            disabled={!leaveType || (mode === "edit" && allocated === "")}
            onClick={submit}
          >
            {mode === "edit" ? "Save" : "Clear balance"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default BalanceRowEditModal;
